import { journalEntriesMolecule } from '@web-app/atoms/in-session/journalEntriesMolecule';
import { useMolecule } from 'bunshi/react';
import { useAtomValue } from 'jotai';
import { FC } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { SidePaneTab } from './SidePaneTab';
import type { SidePaneTabConfig } from './types';

const getBadgeText = (
  journalActivity: { count: number; hasMore: boolean } | null,
): string | undefined => {
  if (!journalActivity || journalActivity.count === 0) {
    return undefined;
  }
  return journalActivity.hasMore
    ? `${journalActivity.count}+`
    : `${journalActivity.count}`;
};

interface JournalSidePaneTabInnerProps {
  tab: SidePaneTabConfig;
}

/**
 * Reads journal activity for the tab badge. Kept inside an error boundary so
 * invalid date / parsing issues in derived journal state cannot crash the
 * entire side pane.
 */
const JournalSidePaneTabInner: FC<JournalSidePaneTabInnerProps> = ({ tab }) => {
  const { journalEntriesSinceLastAppointmentAtom } = useMolecule(
    journalEntriesMolecule,
  );
  const journalActivity = useAtomValue(journalEntriesSinceLastAppointmentAtom);
  const badge = getBadgeText(journalActivity);

  return (
    <SidePaneTab label={tab.label} badge={badge} data-testid={tab.testId} />
  );
};

interface JournalSidePaneTabProps {
  tab: SidePaneTabConfig;
}

/**
 * Journal tab in the side pane tab list — wraps badge logic in react-error-boundary
 * so failures stay on this tab control instead of the app root.
 */
export const JournalSidePaneTab: FC<JournalSidePaneTabProps> = ({ tab }) => {
  return (
    <ErrorBoundary
      fallback={<SidePaneTab label={tab.label} data-testid={tab.testId} />}
    >
      <JournalSidePaneTabInner tab={tab} />
    </ErrorBoundary>
  );
};
